import { useEffect, useRef } from 'react';
import uuid from 'uuid/v4';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { Props } from './useDisabledAnalyticsTypes';
import { usePrevious } from './usePrevious';

const getDisabledReason = ({
	shouldShowNewContentHistory,
	shouldRenderPreview,
	isTitleFocused,
	collabEditError,
	collabLimitReached,
	heartbeatError,
	saveDraftError,
}): string => {
	if (shouldRenderPreview) {
		return 'should-render-preview';
	} else if (shouldShowNewContentHistory) {
		return 'show-new-content-history';
	} else if (isTitleFocused) {
		return 'title-focused';
	} else if (collabLimitReached) {
		return 'collab-limit-reached';
	} else if (collabEditError) {
		return collabEditError.code;
	} else if (heartbeatError) {
		return heartbeatError.reason;
	} else if (saveDraftError) {
		return `Draft sync returned status ${saveDraftError?.statusCode}`;
	}

	return '';
};
export const useDisabledAnalytics = ({
	contentId,
	disabled,
	shouldRenderPreview,
	shouldShowNewContentHistory,
	isTitleFocused,
	collabEditError,
	collabLimitReached,
	heartbeatError,
	saveDraftError,
}: Props) => {
	// Navigator.onLine: https://developer.mozilla.org/en-US/docs/Web/API/NavigatorOnLine/onLine
	// Document.visibilityState: https://developer.mozilla.org/en-US/docs/Web/API/Document/visibilityState
	const onLine = navigator.onLine;
	const visibilityState = document.visibilityState;
	const disabledReason = getDisabledReason({
		shouldRenderPreview,
		shouldShowNewContentHistory,
		isTitleFocused,
		collabEditError,
		collabLimitReached,
		heartbeatError,
		saveDraftError,
	});

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const prevDisabled = usePrevious(disabled);

	// Ref to keep track of enabled/disabled transition start/end and duration
	const enabledTransitionStart = useRef<number | undefined>();
	const disabledTransitionStart = useRef<number | undefined>();
	const disabledTransitionId = useRef<string | undefined>();

	const sinceDisabled = () => {
		return !disabled && typeof disabledTransitionStart.current === 'number'
			? performance.now() - disabledTransitionStart.current
			: undefined;
	};
	const sinceEnabled = () => {
		return disabled && typeof enabledTransitionStart.current === 'number'
			? performance.now() - enabledTransitionStart.current
			: undefined;
	};

	const disabledChanged =
		typeof disabled === 'boolean' && typeof prevDisabled === 'boolean' && disabled !== prevDisabled;

	useEffect(() => {
		if (disabledChanged) {
			// fire analytics event
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					actionSubject: 'editor',
					action: disabled ? 'disabled' : 'enabled',
					source: 'editorComponent',
					attributes: {
						contentId,
						transitionId: disabledTransitionId.current,
						visibilityState,
						onLine,
						disabledReason,
						disabledDuration: sinceDisabled(),
						enabledDuration: sinceEnabled(),
					},
				},
			}).fire();

			// transition from enabled -> disabled
			if (!prevDisabled && disabled) {
				disabledTransitionStart.current = performance.now();
				enabledTransitionStart.current = undefined;

				// transition from disabled -> enabled
			} else {
				disabledTransitionId.current = uuid();
				enabledTransitionStart.current = performance.now();
				disabledTransitionStart.current = undefined;
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [disabledChanged, disabled]);
};
